/**
 * The Dashboard (spec 17). One page, two data states — `Dashboard24h` and
 * `Dashboard30d` are **one component**, not two pages.
 *
 * This page has no forms and **saves nothing**. What carries over from the
 * settings specs is the shell, the palette, card and row anatomy, derived counts
 * and the empty/error discipline — not the save model or dirty state.
 */

export type Period = '24h' | '30d';

/* ------------------------------------------------------------------ *
 * §10 — the four empty states, which are NOT interchangeable.
 * ------------------------------------------------------------------ */

/**
 * **Absent is not zero.** `not_set_up` and `unknown` never render as a number,
 * because a 0 meaning *we do not know* is indistinguishable from a 0 meaning
 * *nothing happened* — and the two lead a practice to opposite conclusions about
 * whether ELVA is working (P345, and the eligibility report's rule P324).
 */
export type MetricState =
  /** A real figure. */
  | { state: 'ok'; value: number }
  /** The period really was empty. Zero is the true answer, so show zero. */
  | { state: 'zero' }
  /** Enough to count, not enough to rate — under 20 in the denominator. */
  | { state: 'too_few'; value: number }
  /** Never configured. **Never a number.** */
  | { state: 'not_set_up'; setupHref: string }
  /** Could not be read. **Never a number.** */
  | { state: 'unknown'; lastGoodRead: string };

/** Under this many in the denominator, a derived percentage means nothing. */
export const TOO_FEW_THRESHOLD = 20;

/* ------------------------------------------------------------------ *
 * §5.1 — the four tiles.
 * ------------------------------------------------------------------ */

export interface DashTiles {
  conversations: MetricState;
  afterHours: number;
  appointments: MetricState;
  /** The three agents that put a visit on the calendar (P333). The TC's accepted
   * treatment and A/R's collected money are **not** in production booked. */
  appointmentsBy: { receptionist: number; sms: number; email: number; recall: number; gapFiller: number };
  /** Cents. `$0` and not a dash when nothing was booked — nothing booked is
   * worth nothing. */
  productionCents: MetricState;
  reschedules: number;
  neededAHuman: MetricState;
  warmTransfers: number;
  urgentTriage: number;
  /** Always now, never scoped — *3 people are still waiting* means the same thing
   * on a day view and a month view. */
  stillWaitingNow: number;
}

/* ------------------------------------------------------------------ *
 * §5.2 — dispositions. Seven, and they are the complete set.
 * ------------------------------------------------------------------ */

export type DispositionKey =
  | 'booked'
  | 'moved'
  | 'answered'
  | 'message'
  | 'warm_transfer'
  | 'urgent_triage'
  | 'cancelled';

/**
 * **There is no `Other` row and there must never be one.** Every conversation
 * lands in exactly one of the seven. If the back end sends something unmapped,
 * that is a bug to surface, not a bucket to invent.
 */
export const DISPOSITIONS: { key: DispositionKey; label: string; colour: string; target: string }[] = [
  { key: 'booked', label: 'Booked an appointment', colour: '#4C5BD4', target: '/communications?d=booked' },
  { key: 'moved', label: 'Moved an appointment', colour: '#7B8AE6', target: '/communications?d=moved' },
  {
    key: 'answered',
    label: 'Answered and nothing else needed',
    colour: '#2E9E6B',
    target: '/communications?d=answered',
  },
  { key: 'message', label: 'Took a message', colour: '#98A0B2', target: '/communications?d=message' },
  { key: 'warm_transfer', label: 'Warm transfer to a human', colour: '#D9A22B', target: '/warm-transfer' },
  { key: 'urgent_triage', label: 'Urgent triage', colour: '#BE4440', target: '/triage' },
  { key: 'cancelled', label: 'Cancelled an appointment', colour: '#C3C9D6', target: '/communications?d=cancelled' },
];

/* ------------------------------------------------------------------ *
 * §5.3 — what the appointments were for.
 * ------------------------------------------------------------------ */

export interface ReasonRow {
  reason: string;
  booked: number;
  /**
   * Requests **ELVA could not turn into a booking** — no slot inside the rules,
   * or no appointment reason mapped. **A caller who changed their mind is not a
   * miss** (P338). Get this wrong and the number becomes an unfalsifiable
   * complaint about patients rather than something a practice can fix.
   */
  asked: number;
  valueCents: number;
}

/* ------------------------------------------------------------------ *
 * §5.4 — the next seven days. RIGHT NOW.
 * ------------------------------------------------------------------ */

export interface ScheduleDay {
  label: string;
  date: string;
  /** Booked chair-hours (spec 42) — rendered "27 h of 30 h". */
  booked: number;
  /** Chair-hours the providers can take. */
  capacity: number;
  /** Free blocks of ≥30 min in a provider's day (Gap Filler's setting). */
  openGaps: number;
  /** A closed week is real data: the ring reads `closed`, not 0%. */
  closed?: boolean;
}

export interface DashSchedule {
  /** `not_set_up` when no operatories exist; `unknown` when the sync is behind. */
  state: 'ok' | 'not_set_up' | 'unknown';
  days: ScheduleDay[];
  /** Of the open gaps, how many Gap Filler is actually working. */
  gapsWorked: number;
  /** The rest, split two ways so each has its own fix (spec 42 §2):
   * no appointment type fits (→ Appointment reasons), and no rule covers
   * (→ Gap Filler rules). `working + noReason + noRule = total open gaps`. */
  gapsNoReason: number;
  gapsNoRule: number;
  /** Total unfillable (= noReason + noRule) — the one number the Gap Filler
   * agent row shows (§6.5). */
  gapsUnreachable: number;
  lastGoodRead?: string;
  setupHref?: string;
}

/* ------------------------------------------------------------------ *
 * §6 — the status vocabulary. This replaces free text in the attention cell.
 * ------------------------------------------------------------------ */

/**
 * Four kinds, and only four (P340). **The kind picks the colour and icon; the
 * code picks the words.** The front end never composes a sentence.
 */
export type AttentionKind = 'blocked' | 'stalled' | 'handoff' | 'clear';

/** `blocked` > `stalled` > `handoff` > `clear`. Highest kind wins (P342). */
export const KIND_RANK: Record<AttentionKind, number> = {
  blocked: 3,
  stalled: 2,
  handoff: 1,
  clear: 0,
};

export interface Attention {
  kind: AttentionKind;
  /** From the vocabulary. An unrecognised code renders **nothing at all** — not
   * the raw code, not a fallback sentence. A made-up status is worse than a blank
   * one. */
  code: string;
  /** 0 for `clear`. */
  count: number;
  /** ISO. How long this has been true — drives tie-breaks and the clock. */
  since: string;
  target: string;
}

export type AgentKey =
  | 'receptionist'
  | 'sms'
  | 'email'
  | 'reminders'
  | 'recall'
  | 'gap_filler'
  | 'treatment'
  | 'invisalign'
  | 'ar';

export type AgentState = 'working' | 'blocked' | 'off' | 'not_set_up';

export interface AgentRow {
  key: AgentKey;
  name: string;
  icon: string;
  /** **Only `blocked` changes the chip.** A stalled agent is still `Working`,
   * which is exactly why the row has to say what is stuck. */
  state: AgentState;
  /** Period-scoped. */
  figures: { value: MetricState; label: string }[];
  /** Cents, where the agent has a money figure at all. */
  money?: { value: MetricState; label: string };
  /** **Not period-scoped** (P344). Several may be true; precedence decides one. */
  attention: Attention[];
  target: string;
}

/* ------------------------------------------------------------------ *
 * §5.6 / §5.7 — the two RIGHT NOW panels at the bottom.
 * ------------------------------------------------------------------ */

export interface ReviewRow {
  label: string;
  count: number;
  /** Which Train ELVA queue the row counts — each row opens its own, rather than
   * four rows all landing on the same page (spec 19 §5). */
  queue: string;
}

export interface RestraintRows {
  optedOut: number;
  outboundTotal: number;
  heldByCap: MetricState;
  /** Always now — a reason with no role mapped cannot be booked at all. */
  uncoveredReasons: number;
}

/* ------------------------------------------------------------------ *
 * The page's whole payload.
 * ------------------------------------------------------------------ */

export interface DashboardData {
  period: Period;
  /** Last data refresh. */
  asOf: string;
  /** The window, in mono, beneath the stamp. */
  windowLabel: string;
  tiles: DashTiles;
  dispositions: Record<DispositionKey, number>;
  reasons: ReasonRow[];
  /** RIGHT NOW. */
  schedule: DashSchedule;
  /** RIGHT NOW in its last column only. */
  agents: AgentRow[];
  /** RIGHT NOW. Sums to the Train ELVA nav badge. */
  review: ReviewRow[];
  restraint: RestraintRows;
}
